import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { productByKey } from "@/lib/products";
import SanaaMark from "@/components/SanaaMark";
import { CheckCircle2, Loader2, ArrowRight, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const free = params.get("free") === "true";
  const freeProduct = params.get("product");
  const freeTier = params.get("tier");

  const [status, setStatus] = useState(free ? "complete" : "polling");
  const [paymentStatus, setPaymentStatus] = useState(free ? "no_payment_required" : "unpaid");
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const stopped = useRef(false);

  useEffect(() => {
    if (free || !sessionId) return;
    let cancelled = false;
    const MAX = 6;
    let count = 0;

    const poll = async () => {
      if (cancelled || stopped.current) return;
      count += 1;
      setAttempts(count);
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        setData(r.data);
        setStatus(r.data.status);
        setPaymentStatus(r.data.payment_status);
        if (r.data.payment_status === "paid" || r.data.status === "expired") {
          stopped.current = true;
          return;
        }
      } catch {
        // ignore — keep polling
      }
      if (count < MAX && !stopped.current) {
        setTimeout(poll, 2000);
      } else {
        stopped.current = true;
        if (paymentStatus !== "paid") setStatus("timeout");
      }
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, free]);

  const productKey = data?.product || freeProduct;
  const productMeta = productKey ? productByKey(productKey) : null;
  const tier = data?.tier || freeTier;

  const polling = !free && paymentStatus !== "paid" && status !== "expired" && status !== "timeout";
  const success = free || paymentStatus === "paid";
  const failed = status === "expired" || status === "timeout";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border/60">
        <div className="mx-auto max-w-5xl px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <SanaaMark size={26} />
            <span className="font-display tracking-tight">Sanaa AI</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="max-w-xl w-full text-center">
          {polling && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-secondary flex items-center justify-center mb-6">
                <Loader2 className="h-7 w-7 animate-spin" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Processing payment</div>
              <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight">Confirming with Stripe…</h1>
              <p className="mt-4 text-muted-foreground">Check {attempts} of 6 — usually takes 2–6 seconds.</p>
            </>
          )}

          {success && productMeta && (
            <>
              <div
                className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-6"
                style={{ background: productMeta.gradient, color: "white" }}
              >
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{free ? "Activated" : "Payment confirmed"}</div>
              <h1 data-testid="success-title" className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight">
                Welcome to <span style={{ color: productMeta.accent }}>{productMeta.name}</span>.
              </h1>
              <p className="mt-4 text-muted-foreground">
                Your <span className="text-foreground font-medium capitalize">{tier}</span> plan is active.
                {!free && data && (
                  <> A receipt for <span className="font-mono">${data.amount.toFixed(2)}</span> {data.currency.toUpperCase()} has been recorded.</>
                )}
              </p>
              <div className="mt-10 flex flex-wrap gap-3 justify-center">
                <button
                  data-testid="success-open-product"
                  onClick={() => nav(productMeta.route)}
                  className="inline-flex h-11 items-center gap-2 rounded-sm px-5 text-sm font-medium text-white"
                  style={{ background: productMeta.gradient }}
                >
                  Open {productMeta.name} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </button>
                <button
                  onClick={() => nav("/app/billing")}
                  data-testid="success-back-billing"
                  className="inline-flex h-11 items-center rounded-sm border border-border px-5 text-sm font-medium hover:bg-card"
                >
                  Back to billing
                </button>
              </div>
            </>
          )}

          {failed && !success && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-6">
                <XCircle className="h-7 w-7 text-destructive" />
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Payment not confirmed</h1>
              <p className="mt-4 text-muted-foreground">
                We couldn't confirm your payment with Stripe.
                {status === "expired" ? " The session expired." : " You can try again — nothing was charged."}
              </p>
              <button
                onClick={() => nav("/app/billing")}
                className="mt-8 inline-flex h-11 items-center rounded-sm bg-foreground px-5 text-sm font-medium text-background"
              >
                Return to billing
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
